import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';
import { pillarArticles } from '@/mocks/articles';

export default function DynamicArticle() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\/|\/$/g, '');
  const article = pillarArticles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6 py-20">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">Article Not Found</h1>
              <p className="text-foreground-600 mb-6">The article you are looking for does not exist or may have been moved.</p>
              <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-md hover:bg-primary-600 transition-all cursor-pointer">
                Back to Homepage
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20">
        <article className="w-full px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-3xl mx-auto">
            <nav className="mb-8" aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <Link to="/" className="text-accent-600 hover:text-accent-700 transition-colors cursor-pointer">Home</Link>
                </li>
                <li className="text-foreground-400">/</li>
                <li className="text-foreground-600">{article.title.length > 40 ? article.title.substring(0, 40) + '...' : article.title}</li>
              </ol>
            </nav>

            <header className="mb-10">
              <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-4">
                {article.badge}
              </span>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-base text-foreground-600 leading-relaxed">
                {article.metaDescription}
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-5 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i> Updated August 2026
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i> 8 min read
                </span>
              </div>
            </header>

            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  '@context': 'https://schema.org',
                  '@type': 'Article',
                  headline: article.title,
                  description: article.metaDescription,
                  url: `${import.meta.env.VITE_SITE_URL}/${article.slug}`,
                  datePublished: '2026-08-18',
                  dateModified: '2026-08-18',
                  author: { '@type': 'Organization', name: 'Basetao Spreadsheet Hub' },
                  publisher: { '@type': 'Organization', name: 'Basetao Spreadsheet Hub' },
                  mainEntityOfPage: {
                    '@type': 'WebPage',
                    '@id': `${import.meta.env.VITE_SITE_URL}/${article.slug}`,
                  },
                }),
              }}
            />

            <div className="space-y-8">
              {article.intro.map((paragraph, idx) => (
                <p key={idx} className="text-foreground-700 leading-relaxed">{paragraph}</p>
              ))}

              {article.sections.map((section, sIdx) => (
                <section key={sIdx}>
                  <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4 mt-10">
                    {section.heading}
                  </h2>
                  {section.content.map((paragraph, pIdx) => (
                    <p key={pIdx} className="text-foreground-700 leading-relaxed mb-4">{paragraph}</p>
                  ))}
                </section>
              ))}
            </div>

            <div className="mt-10 p-6 bg-accent-50 rounded-lg border border-accent-200 text-center">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">Ready to Get Started?</h3>
              <p className="text-sm text-foreground-600 mb-4">
                Visit the official Basetao store and put your spreadsheet knowledge into action today.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-md hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  Visit Official Store
                </a>
                <Link
                  to="/"
                  className="px-6 py-3 border border-accent-300 text-accent-700 font-medium text-sm rounded-md hover:bg-accent-100 transition-all whitespace-nowrap cursor-pointer"
                >
                  Back to Homepage
                </Link>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-background-200/60">
              <h4 className="text-sm font-semibold text-foreground-900 mb-3">Related Resources</h4>
              <div className="flex flex-wrap gap-2">
                <Link to="/" className="text-sm text-accent-600 hover:text-accent-700 bg-accent-50 px-3 py-1.5 rounded-md transition-colors cursor-pointer">Basetao Spreadsheet Home</Link>
                <Link to="/basetao-spreadsheet-guide" className="text-sm text-foreground-600 hover:text-foreground-900 bg-background-100 px-3 py-1.5 rounded-md transition-colors cursor-pointer">Complete Guide</Link>
                <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-sm text-foreground-600 hover:text-foreground-900 bg-background-100 px-3 py-1.5 rounded-md transition-colors cursor-pointer">Official Store</a>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}